import type { MunicipalityCount, RoadUser } from '../../src/api/queries';
import type { TrafficCalibration } from '../../src/api/traffic-queries';
import type {
  Impairment,
  InjuryDisposition,
  RoadRole,
  TallyMetric,
  VehicleKind,
} from '../../src/extract/traffic-taxonomy';
import type { IncidentFlag } from '../../src/flags';
import type { Prefecture } from '../../src/parse/classify';
import { type County, countiesInPrefecture, countyRank } from '../../src/parse/counties';
import type { Source } from '../../src/source';
import {
  UNSPECIFIED_COUNTY,
  UNSPECIFIED_MUNICIPALITY,
  UNSPECIFIED_PREFECTURE,
} from '../../src/wire';
import type { Claim } from './coverage';
import { formatCount, formatDate, formatEuros } from './format';

/**
 * The Estonian words this dashboard uses for machine values — categories, aggregate metrics,
 * granularities and the prefectures. Display labels only: `src/extract/taxonomy.ts` owns the enum
 * and its internal English, so a page can be reworded without touching the prompt, the schema or
 * the database. They live here because the landing cards and the pages they open must agree.
 */

/**
 * All 19 taxonomy values, in `CATEGORIES` order.
 *
 * Most labels are the corpus's own word (the cues in `taxonomy.ts`) in the plural, because a card
 * counting 4 300 rows reads as a heading over a list, not as a single event. Three deserve a note:
 *
 *  - `PROPERTY_DAMAGE` — the penal-code phrase is "asja rikkumine ja hävitamine", which is a
 *    mouthful on a card; "varakahjustused" is the ordinary Estonian noun for the same thing.
 *  - `PUBLIC_ORDER` — the category holds *offences*, and the cue is "avaliku korra rikkumine", so
 *    a bare "Avalik kord" would name the value being protected rather than the events counted.
 *  - `DEATH_INVESTIGATION` — the row is explicitly "(no crime alleged)", so the label must not
 *    imply one. "Surmajuhtumid" says what the entries are: deaths the police reported on.
 *
 * `NOT_AN_INCIDENT` is present for completeness — `/api/meta` never returns it and the landing
 * page skips it — but `/kategooria/NOT_AN_INCIDENT` is a URL a reader can type, and a page titled
 * with a raw enum value is worse than one titled honestly.
 */
export const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  THEFT: 'Vargused',
  ROBBERY: 'Röövimised',
  FRAUD: 'Kelmused',
  VEHICLE_ACCIDENT: 'Liiklusõnnetused',
  TRAFFIC_OFFENCE: 'Liiklusrikkumised',
  DOMESTIC_VIOLENCE: 'Lähisuhtevägivald',
  ASSAULT: 'Kehalised väärkohtlemised',
  PROPERTY_DAMAGE: 'Varakahjustused',
  BORDER_SECURITY: 'Piiriturvalisus',
  DRUGS: 'Narkokuriteod',
  WEAPONS: 'Relvakuriteod',
  PUBLIC_ORDER: 'Avaliku korra rikkumised',
  SEXUAL_OFFENCE: 'Seksuaalkuriteod',
  HOMICIDE: 'Tapmised',
  DEATH_INVESTIGATION: 'Surmajuhtumid',
  MISSING_PERSON: 'Kadunud isikud',
  ECONOMIC_CRIME: 'Majanduskuriteod',
  OTHER: 'Muud juhtumid',
  NOT_AN_INCIDENT: 'Ei ole juhtum',
};

/** The category enum value a page never wants to show: the extractor's "this was boilerplate". */
export const NOT_AN_INCIDENT = 'NOT_AN_INCIDENT';

/**
 * The Estonian label for a category, or the raw value when the taxonomy has grown a member this
 * map has not. Showing `NEW_CATEGORY` is ugly; showing nothing hides a whole slice of the corpus.
 */
export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/**
 * The categories that have a hand-built page of their own (spec §7); everything else goes to
 * `/kategooria/:category`. **`VEHICLE_ACCIDENT` is deliberately absent**: `/liiklusonnetused` is the
 * Liiklusinfo log, a different corpus with a different number in it, and the card counts digest rows.
 */
export const CATEGORY_ROUTES: Readonly<Record<string, string>> = {
  THEFT: '/vargused',
  FRAUD: '/kelmused',
  DOMESTIC_VIOLENCE: '/lahisuhtevagivald',
};

/** Where a category card links: its own page when it has one, the generic page otherwise. */
export function categoryPath(category: string): string {
  return CATEGORY_ROUTES[category] ?? `/kategooria/${encodeURIComponent(category)}`;
}

/**
 * A category's label where it sits beside this site's own pages, which is the only place two
 * different things can both be called "Liiklusõnnetused": the nav's page is the Liiklusinfo log,
 * and this row is the day chronicle's own accident paragraphs — a different series, another number.
 */
export function navCategoryLabel(category: string): string {
  const qualifier = NAV_LABEL_QUALIFIERS[category];
  return qualifier === undefined
    ? categoryLabel(category)
    : `${categoryLabel(category)} (${qualifier})`;
}

const NAV_LABEL_QUALIFIERS: Readonly<Record<string, string>> = {
  VEHICLE_ACCIDENT: 'päevakroonikas',
};

/**
 * `incidents.subcategory` in Estonian.
 *
 * The column holds a **free-text English slug** the extractor invents per incident — the schema
 * asks for "a short English slug for a finer type, e.g. `burglary`, `bicycle_theft`, `phone_scam`,
 * `bank_impersonation`, `illegal_stay`" (spec §4.3). It is an open set by construction, so this map
 * can never be complete; it covers exactly the slugs the spec and the taxonomy name as examples,
 * and `subcategoryLabel` returns `null` for everything else rather than guessing.
 *
 * Deliberately **not** extended with plausible-looking slugs the extractor might emit
 * (`shoplifting`, `drunk_driving`, …). The extraction prompt is Epic 3 and does not exist on this
 * branch; a map written against imagined output would be a pile of translations nobody has ever
 * seen a real value for.
 *
 * Lower case, because these are common nouns appearing mid-line after the category name.
 */
export const SUBCATEGORY_LABELS: Readonly<Record<string, string>> = {
  burglary: 'sissemurdmine',
  bicycle_theft: 'jalgrattavargus',
  phone_scam: 'telefonikelmus',
  bank_impersonation: 'panga nimel esinemine',
  illegal_stay: 'ebaseaduslik riigis viibimine',
};

/**
 * The Estonian word for a subcategory slug, or `null` when this map does not know it.
 *
 * `null` is a real answer, not a failure. The caller must not drop an unknown slug — it is data a
 * reader may want — and must not paste it into Estonian prose either, because it is English.
 * `IncidentCard` resolves that by showing an unmapped slug as a machine-styled code chip, which is
 * honest about what it is.
 */
export function subcategoryLabel(slug: string): string | null {
  return SUBCATEGORY_LABELS[slug] ?? null;
}

/**
 * `daily_aggregates.metric` in Estonian — the police's own tally names (spec §5.2).
 *
 * These are counts *of police reports*, which is why they are never mixed with incident counts:
 * "teated" is how many reports came in, not how many events happened. `AggregateChart` carries a
 * caption saying so.
 */
export const METRIC_LABELS: Readonly<Record<string, string>> = {
  reports: 'Teateid',
  incidents: 'Juhtumeid',
  damage: 'Kahju',
  detentions: 'Kinnipidamisi',
  checks: 'Kontrolle',
  calls: 'Väljakutseid',
};

/** The Estonian word for a metric, from either series' vocabulary: `AggregateChart` is shared and
 * reads its metric out of the API's answer, and the two vocabularies do not collide. */
export function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? TRAFFIC_METRIC_LABELS[metric as TallyMetric] ?? metric;
}

/**
 * The review flags, in Estonian.
 *
 * A flag is a note to whoever runs `pnpm cli review`, not a verdict on the incident — the row is
 * shown either way, and nothing on the site hides flagged data. But it is *visible* to readers, in
 * the card's tooltip, and `money_mismatch` is machine text in a page that has no English anywhere
 * else. Each label says what the machine noticed, in the same register as the rest of the card:
 * lower case, no jargon, and no implication that the police got something wrong — most of these are
 * the extractor doubting itself, not the source.
 *
 * Typed as `Record<IncidentFlag, string>` — the whole vocabulary `src/flags.ts` owns, which is what
 * `unionFlags` writes into `incidents.flags` and `daily_aggregates.flags` — so a flag added to any of
 * its sub-unions is a compile error here rather than a code leaking into an Estonian tooltip.
 */
export const FLAG_LABELS: Readonly<Record<IncidentFlag, string>> = {
  money_mismatch: 'summa ei kattu teate tekstis olevaga',
  date_mismatch: 'kuupäev ei kattu teate tekstis olevaga',
  // The date-resolution flags. None of them is the extractor doubting itself: `date_in_future`
  // says the text dated an event after the post that carries it, `stated_date_far` says the
  // resolved date is unusually old — and kept as it was — and `year_inferred_back` says where the
  // year came from. The digest never writes a year, so on most of the corpus the machine picked
  // one; when it had to step that year backwards to make the day fit, the reader is told, because
  // a post whose own publication date is out of order can carry a whole page of entries a year
  // into the past. The label says what happened, not that anything is wrong.
  date_in_future: 'teates kirjas kuupäev on avaldamisest hilisem',
  stated_date_far: 'kuupäev on avaldamisest tunduvalt varasem',
  year_inferred_back: 'aastaarv on tuletatud avaldamisajast, mitte teate tekstist',
  no_date: 'teate tekstis ei olnud juhtumi kuupäeva',
  category_unknown: 'kategooria jäi ebaselgeks',
  outlier_damage: 'ebatavaliselt suur kahjusumma',
  heading_ambiguous: 'pressiteate vahepealkirja tõlgendus oli ebaselge',
  // The traffic log now and then mistypes the date it opens on — a year with three digits, or a
  // space where the dot belongs. The entry is read anyway, and this says the reader should look at
  // the date twice: where the mistyped part was the year, the year shown is the one inferred from
  // the publication day.
  date_typo: 'teate kuupäev oli kirjavea tõttu puudulik',
  prefecture_inferred: 'prefektuur on tuletatud maakonna järgi',
  report_date_from_text: 'kokkuvõtte periood on võetud teate tekstist',
  weekend_tally: 'kokkuvõte katab nädalavahetuse, mitte ühte ööpäeva',
  scope_mismatch: 'kokkuvõtte ulatus jäi ebaselgeks',
  // The one flag that says the police claimed *less* than the row does: the zero was stated only
  // about a restricted kind of event, and the row records it as a plain none.
  qualified_count: 'kokkuvõtte null kehtis ainult teates nimetatud kitsenduse kohta',
  // The four liiklusinfo flags: three disagreements the projection refused to resolve — the police
  // wrote both numbers — and one that says where a päevanumber's date came from.
  scooter_mismatch: 'teate tekst ja loetud sõidukite loend ei ole tõukeratta osas kooskõlas',
  injury_count_mismatch: 'teates öeldud kannatanute arv ei lange kokku kirjeldatud isikute arvuga',
  tally_mismatch: 'politsei päevanumber ei lange kokku sama päeva teadete arvuga',
  report_date_from_paragraphs:
    'päevanumbri kuupäev on võetud teate lõikudest, mitte avaldamispäevast',
};

/**
 * The Estonian sentence for a flag, or the raw code when this map has not heard of it.
 *
 * The fallback is the same bargain `categoryLabel` makes: an untranslated code is ugly and honest,
 * and hiding the flag would hide the one signal on the card that says "check this one against the
 * source".
 */
export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag as IncidentFlag] ?? flag;
}

/**
 * What a damage badge owes the reader when the query left flagged money out of it (audit D5).
 *
 * `queries.ts` excludes the incidents whose euro figure its own cross-check doubts — 12 of 961 rows
 * and 15.1% of the money on the corpus this was measured against — and an exclusion nobody is told
 * about is its own dishonesty: the number simply shrinks, and no reader can tell that from a quiet
 * quarter. So the amount and the count are printed under the total they were taken out of, in the
 * same register the flag tooltips use: the machine marked these for checking, which is not a claim
 * that the police wrote a wrong number.
 *
 * `null` — not an empty string — is the answer when nothing was excluded, so a caller that forgets
 * to branch renders nothing rather than a bare full stop, and the badge's type can demand the field
 * without demanding a sentence that would be false.
 */
export function excludedDamageNote(cents: number, count: number): string | null {
  if (count <= 0) return null;
  // The singular takes the elative of `üks`, as `damageDenominator` takes the nominative for one.
  const incidents = count === 1 ? 'ühest juhtumist' : `${formatCount(count)} juhtumist`;
  return `Välja on jäetud ${formatEuros(cents)} ${incidents}, mille kahjusumma on märgitud kontrollimist vajavaks.`;
}

/**
 * The same exclusion again, for a **police tally** rather than for incidents (audit D5, Task 49).
 *
 * The sentence cannot be {@link excludedDamageNote}'s, because that one counts *juhtumeid* and these
 * rows are not incidents — they are the police's own daily lines, and one of them can state a whole
 * day's money in a single number. On the corpus the one flagged tally is also the only damage row
 * its bucket has, so a silent exclusion would leave a count with no money beside it, which reads as
 * a digest that stated no sum at all.
 *
 * No row count appears: the reader has no denominator to compare it against, and "ühest reast" is
 * machine bookkeeping rather than a fact about the police's report.
 */
export function excludedTallyDamageNote(cents: number, count: number): string | null {
  if (count <= 0) return null;
  return `Välja on jäetud ${formatEuros(cents)}, mis on märgitud kontrollimist vajavaks.`;
}

/**
 * What a hatched bar in the tally panel means (audit M3, Task 49).
 *
 * Two kinds of row earn the hatch and the sentence has to be true of both: a weekend tally, and a
 * digest that states its own period ("Ajavahemikul …"). What they share is all the sentence claims —
 * the police published one figure for more than one day, and it is carried on the one date the row
 * has. Nothing is said about *which* day of the period that date is, because the projection knows
 * only where the row was dated, not where the period began.
 *
 * It describes the *tally*, not the bar's width, so it stays true at week and month granularity.
 */
export const MULTI_DAY_TALLY_NOTE =
  'Viirutatud tulba arv pärineb kokkuvõttest, mis katab rohkem kui ühe ööpäeva: nädalavahetuse või mõne muu politsei nimetatud ajavahemiku. Politsei avaldas selle kohta ühe arvu ja see on kantud ühele kuupäevale.';

/**
 * The same fact in a clause, for the tooltip that opens on such a bar.
 *
 * The hatch is a pattern, and a pattern has to be decoded against a note the reader may have
 * scrolled past. The number itself is read here, so the caveat is repeated here — short, because a
 * tooltip is a label and not a paragraph.
 */
export const MULTI_DAY_TALLY_TOOLTIP = 'kokkuvõte katab rohkem kui ühe ööpäeva';

/**
 * Why a bucket the police did publish a number for shows none (audit Task 51).
 *
 * On 2026-08-05 the police reported no fraud reports *in which people suffered property damage*.
 * `daily_aggregates` carries that as `count = 0`, which claims more than the sentence did, so the
 * bucket states no figure at all and this says why. The corpus's own qualifier is quoted as an
 * example rather than as the rule, because the note has to stay true of the next one too.
 *
 * It names neither a day nor a single bucket: the same mark is drawn at week and month granularity,
 * where one bar covers many days and a window can hold several marked bars. "Mõne ajavahemiku kohta"
 * is true of one bucket and of five, whatever period a bucket happens to be.
 */
export const QUALIFIED_TALLY_NOTE =
  'Mõne ajavahemiku kohta ei ole siin arvu: politsei ütles nulli ainult kitsendatud kujul — näiteks „ühtegi teadet, mille puhul oleks inimestele varalist kahju tekitatud“. Üldist nulli nad ei avaldanud, nii et seda siin ka ei näidata.';

/** The same fact in a clause, for the tooltip that opens where the number would have been read. */
export const QUALIFIED_TALLY_TOOLTIP =
  'politsei null kehtis ainult teates nimetatud kitsenduse kohta';

/**
 * The marks' key under a chart: two or three words each, beside a swatch of the actual paint.
 *
 * These are the short forms of the notes above — the legend says which mark is which, the note says
 * what it means — so they carry no glyph of their own: the swatch is drawn, not spelled.
 */
export const MULTI_DAY_TALLY_LEGEND = 'katab mitu ööpäeva';
export const QUALIFIED_TALLY_LEGEND = 'kitsendatud null';
export const UNREAD_SERIES_LEGEND = 'töötlemata';

/**
 * The same exclusion, said the way a **median** is affected by it.
 *
 * A median does not add euros up, so no euro figure was taken out of it: the flagged rows were
 * dropped from the *ranking*, and what moved is which incident sits in the middle. Printing
 * {@link excludedDamageNote} under a median badge would name a sum that was never part of the
 * number above it — a true sentence in the wrong place, which reads as an arithmetic claim the
 * badge does not make. So the median's note counts rows and names no amount at all.
 *
 * `null` when nothing was excluded, on the same argument as `excludedDamageNote`: a badge that
 * always carries a caveat is a badge whose caveat stops being read.
 */
export function excludedMedianNote(count: number): string | null {
  if (count <= 0) return null;
  // Nominative for one, partitive above it — the split `damageDenominator` makes for the same reason.
  const incidents = count === 1 ? 'üks juhtum' : `${formatCount(count)} juhtumit`;
  return `Mediaani arvutusest on välja jäetud ${incidents}, mille kahjusumma on märgitud kontrollimist vajavaks.`;
}

/** Bucket sizes as a reader picks them. */
export const GRANULARITY_LABELS = {
  day: 'Päev',
  week: 'Nädal',
  month: 'Kuu',
} as const;

/** How the no-prefecture bucket is named on screen. The sentinel itself lives in `src/wire.ts`. */
export const UNSPECIFIED_PREFECTURE_LABEL = 'Määramata';

/**
 * The four PPA prefectures, in the words the police's own headings use.
 *
 * `incidents.prefecture` holds the normalized enum (`classify.ts` turns `PÕHJA PREFEKTUUR` into
 * `NORTH`), so this map is the way back. The keys are typed as `Prefecture` — a **type-only** import
 * from the server, erased before the bundler sees it — which makes a fifth prefecture a compile
 * error here rather than a raw `NORTH` appearing in an Estonian dropdown.
 *
 * `run.ts` spells the same four words for the prompt. That is not a duplication worth removing: this
 * copy is display text a design pass may reword ("Põhja prefektuur" → "Põhja-Eesti"), and that copy
 * is prompt input that must stay byte-identical to what the model was shown.
 */
export const PREFECTURE_LABELS: Readonly<Record<Prefecture, string>> = {
  NORTH: 'Põhja prefektuur',
  SOUTH: 'Lõuna prefektuur',
  EAST: 'Ida prefektuur',
  WEST: 'Lääne prefektuur',
};

/**
 * The Estonian name of a prefecture value, including the `UNSPECIFIED` sentinel.
 *
 * An unknown value is returned verbatim for the same reason `categoryLabel` does it: the alternative
 * is a dropdown that silently omits a slice of the corpus. `/api/meta` lists the prefectures the
 * database actually holds, so a value this map has not heard of is data, not a typo.
 */
export function prefectureLabel(value: string): string {
  if (value === UNSPECIFIED_PREFECTURE) return UNSPECIFIED_PREFECTURE_LABEL;
  return PREFECTURE_LABELS[value as Prefecture] ?? value;
}

/** Nothing is selected: every prefecture at once, which is what a page shows by default. */
export const ALL_PREFECTURES_LABEL = 'Kõik prefektuurid';

/** One dropdown entry; `''` is the empty selection. Declared here, not imported from the `.tsx`
 * component, because the server tsconfig compiles this module too and has no `--jsx`. */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * The options a prefecture control should offer, or `null` when it should not be rendered at all.
 *
 * This is the whole decision procedure of the control, kept out of the component so it can be tested
 * without a DOM. Four rules, and each is a case the live database will actually produce:
 *
 *  1. **No prefectures and no filter → `null`, i.e. no control.** `/api/meta` publishes the
 *     prefectures present in `incidents`; the list is empty before extraction has ever run. A
 *     dropdown whose only entries are "kõik" and "määramata" over an empty table is a filter that
 *     can only ever return nothing, so it is not shown (arch checkpoint 3: build the control
 *     "triggered when `meta.prefectures` is non-empty"). The same holds while `/api/meta` is still
 *     in flight, so no control flashes into place under the reader's cursor.
 *  2. **A filter is active → the control is shown regardless.** `?prefecture=NORTH` is reachable by
 *     hand, and a filtered page whose filter has no visible control is the worse failure: the reader
 *     sees a smaller number with nothing to say why, and no way to clear it.
 *  3. **The selected value is always among the options**, even when `/api/meta` does not list it —
 *     a `<select>` whose `value` matches no `<option>` renders blank in every browser, which would
 *     show "all prefectures" while filtering to one.
 *  4. **"Määramata" is hardcoded, always.** `getMeta` cannot report it: the query is
 *     `WHERE prefecture IS NOT NULL`, so the NULL bucket has no entry to publish, and adding one
 *     would mean a second sentinel travelling the wire. The `UNSPECIFIED` sentinel is a filter
 *     `queries.ts` already understands; this is the option that reaches it.
 *
 * Sorted by the Estonian label under `et` collation, not by the machine value — `EAST, NORTH, SOUTH,
 * WEST` reads as a random order once it is printed as `Ida, Põhja, Lõuna, Lääne`.
 */
export function prefectureOptions(
  prefectures: readonly string[],
  selected: string | null,
): SelectOption[] | null {
  if (prefectures.length === 0 && selected === null) return null;

  const known = [...new Set(prefectures)];
  if (selected !== null && selected !== UNSPECIFIED_PREFECTURE && !known.includes(selected)) {
    known.push(selected);
  }

  const named = known
    .map((value) => ({ value, label: prefectureLabel(value) }))
    .sort((a, b) => ET.compare(a.label, b.label));

  return [
    { value: '', label: ALL_PREFECTURES_LABEL },
    ...named,
    { value: UNSPECIFIED_PREFECTURE, label: UNSPECIFIED_PREFECTURE_LABEL },
  ];
}

/**
 * What the traffic log's hospital figure is a count of (design §4, §7.4).
 *
 * The series reports a **disposition** — a decision to drive someone to hospital — and only 78 of
 * its 10 504 paragraphs use severity wording at all. A badge labelled "Vigastatuid" over this
 * number is a claim the source never made, so `StatBadge`'s `disposition` flavour requires a note
 * and this is the note: one constant, on {@link NO_DAMAGE_STATED}'s precedent, so the badge, the
 * facet caption and the feed cannot drift into three different accounts of the same word.
 */
export const HOSPITAL_DISPOSITION_NOTE =
  'Teade ütleb „toimetati haiglasse“ — see on otsus inimene haiglasse viia, mitte hinnang vigastuse raskusele.';

/**
 * The bucket a facet draws for the rows whose field the report never stated.
 *
 * A name, not an absence: `FacetBar` draws this bar whatever its size, because "Teadmata: 1 140" is
 * a measurement of the corpus and dropping it renormalises the facet onto the rows that happened to
 * answer. Overridable per facet — county's unstated bucket is "Määramata" and means something quite
 * different (mostly a road crossing several counties, design §7.5) — but never absent.
 */
export const UNKNOWN_FACET_LABEL = 'Teadmata';

/**
 * The sentence under a facet saying what it was computed over.
 *
 * Disposition is present in 89 % of paragraphs, county in 90 %, impairment in 11 %; bars drawn over
 * only the rows that stated the field would put those three on the same visual footing. So the two
 * numbers are printed: every row the facet covers, and the rows that answered it.
 *
 * `unit` has no default. A facet counts incidents in two of its three uses and people in the third,
 * and a default noun is exactly how the age facet would come to claim it counts incidents.
 * Unconditional, unlike {@link excludedDamageNote}: a denominator that appears only when it is
 * unflattering is one nobody learns to look for.
 */
export function facetDenominator(stated: number, total: number, unit: string): string {
  return `Kokku ${formatCount(total)} ${unit}, neist ${formatCount(stated)} kohta on see teates öeldud.`;
}

/**
 * The two daily series, in the words politsei.ee's own index uses for them.
 *
 * `src/source.ts` owns the values (`'digest' | 'traffic'`); this is the Estonian, kept here for the
 * same reason `CATEGORY_LABELS` is — the enum is a wire fact and the name over a panel is a design
 * decision, and neither should move because the other did. Typed against `Source`, so a third
 * series is a compile error here rather than a raw `traffic` in an Estonian caption.
 */
export const SOURCE_LABELS: Readonly<Record<Source, string>> = {
  digest: 'Politseis registreeritud sündmused',
  traffic: 'Liiklusinfo',
};

/** The same two series, short enough to sit visibly on a chronicle chip. The chronicle is the one
 * surface mixing both, so which series a row came from has to be readable, never hover-only. */
export const SOURCE_CHIP_LABELS: Readonly<Record<Source, string>> = {
  digest: 'Päevateated',
  traffic: 'Liiklusinfo',
};

/**
 * What a panel is drawing: rows a model read out of the prose, or figures the police published.
 *
 * The distinction is `daily_aggregates` against `incidents` — the one this project refuses to let a
 * chart blur — and it needs its own word because the *series* name does not carry it. Both grains
 * of the traffic page truthfully say "Liiklusinfo".
 */
export const GRAINS = ['incidents', 'aggregates'] as const;

export type Grain = (typeof GRAINS)[number];

/**
 * The provenance sentence for each (series, grain) pair — all four written out.
 *
 * Four sentences rather than one built from fragments: they differ in their noun as well as in
 * their clause ("juhtum" against "õnnetus"), and a template that composed them would produce
 * grammatical Estonian only by accident. The series name is interpolated from {@link SOURCE_LABELS}
 * so the four cannot disagree about what the series is called.
 */
export const SOURCE_CAPTIONS: Readonly<Record<Source, Readonly<Record<Grain, string>>>> = {
  digest: {
    incidents: `Allikas: politsei päevateade „${SOURCE_LABELS.digest}“. Iga rida on ühest teatelõigust keelemudeli abil välja loetud juhtum, mitte politsei enda kokkuvõte.`,
    aggregates: `Allikas: politsei päevateade „${SOURCE_LABELS.digest}“. Need arvud avaldas politsei ise; siinsete juhtumitega neid kokku ei liideta.`,
  },
  traffic: {
    incidents: `Allikas: politsei päevateade „${SOURCE_LABELS.traffic}“. Iga rida on ühest teatelõigust keelemudeli abil välja loetud õnnetus, mitte politsei enda kokkuvõte.`,
    aggregates: `Allikas: politsei päevateade „${SOURCE_LABELS.traffic}“. Need arvud avaldas politsei ise; siinsete õnnetustega neid kokku ei liideta.`,
  },
};

// ─── Liiklusinfo ────────────────────────────────────────────────────────────

/** `incident_people.road_role` in Estonian — never `role`, which is the digest's own column. */
export const ROAD_ROLE_LABELS: Readonly<Record<RoadRole, string>> = {
  driver: 'Juht',
  passenger: 'Kaassõitja',
  pedestrian: 'Jalakäija',
  cyclist: 'Jalgrattur',
  scooter_rider: 'Tõukerattur',
  motorcyclist: 'Mootorrattur',
  other: 'Muu liikleja',
  unknown: 'Teadmata',
};

/**
 * `incident_people.injury` — a **disposition**, never a grade of injury (design §4). "Vigastatuid"
 * appears nowhere: only 78 of 10 504 paragraphs use severity wording at all.
 */
export const INJURY_LABELS: Readonly<Record<InjuryDisposition, string>> = {
  killed: 'Hukkus',
  hospital: 'Toimetati haiglasse',
  first_aid: 'Sai kohapeal esmaabi',
  uninjured: 'Ei saanud viga',
  unknown: 'Teadmata',
};

/** `incident_vehicles.kind`. `bus` covers the trolleybus, as the corpus itself does. */
export const VEHICLE_KIND_LABELS: Readonly<Record<VehicleKind, string>> = {
  car: 'Sõiduauto',
  van: 'Kaubik',
  truck: 'Veoauto',
  bus: 'Buss',
  motorcycle: 'Mootorratas',
  moped: 'Mopeed',
  bicycle: 'Jalgratas',
  e_scooter: 'Tõukeratas',
  atv: 'Maastikusõiduk',
  tram: 'Tramm',
  other: 'Muu sõiduk',
  unknown: 'Teadmata sõiduk',
};

/** `incidents.impairment`. Stated per accident, so its denominator is accidents, not people. */
export const IMPAIRMENT_LABELS: Readonly<Record<Impairment, string>> = {
  alcohol: 'Alkohol',
  drugs: 'Narkootikum',
  both: 'Alkohol ja narkootikum',
  none: 'Teade ütles, et joovet ei olnud',
  unknown: 'Teadmata',
};

/**
 * The police's own daily tally names. `alcohol_drivers_beside_drugs` is P3's split key — an alcohol
 * figure stated beside a drug figure is a different measurement, and §8.4 forbids one bar for both.
 * `injured` is the police's `inimkannatanuid`, so it is not a severity either.
 */
export const TRAFFIC_METRIC_LABELS: Readonly<
  Record<TallyMetric | 'alcohol_drivers_beside_drugs', string>
> = {
  injury_accidents: 'Inimkannatanutega õnnetusi',
  scooter_accidents: 'Tõukerattaõnnetusi',
  impaired_drivers: 'Joobes juhte',
  alcohol_drivers: 'Alkoholi tarvitanud juhte',
  alcohol_drivers_beside_drugs: 'Alkoholi tarvitanud juhte (teates koos narkoarvuga)',
  drug_drivers: 'Narkojoobes juhte',
  stolen_or_found_vehicles: 'Varastatud või leitud sõidukeid',
  injured: 'Kannatanuid',
  killed: 'Hukkunuid',
};

/**
 * The fifteen counties and the sentinel. `places.ts` normalises every spelling it meets to exactly
 * these names, so the map is an identity for them and exists for the one key that is not a county.
 */
export const COUNTY_LABELS: Readonly<Record<County | typeof UNSPECIFIED_COUNTY, string>> = {
  Harjumaa: 'Harjumaa',
  Hiiumaa: 'Hiiumaa',
  'Ida-Virumaa': 'Ida-Virumaa',
  Jõgevamaa: 'Jõgevamaa',
  Järvamaa: 'Järvamaa',
  Läänemaa: 'Läänemaa',
  'Lääne-Virumaa': 'Lääne-Virumaa',
  Põlvamaa: 'Põlvamaa',
  Pärnumaa: 'Pärnumaa',
  Raplamaa: 'Raplamaa',
  Saaremaa: 'Saaremaa',
  Tartumaa: 'Tartumaa',
  Valgamaa: 'Valgamaa',
  Viljandimaa: 'Viljandimaa',
  Võrumaa: 'Võrumaa',
  [UNSPECIFIED_COUNTY]: 'Määramata',
};

/** The Estonian name of a county value, sentinel included; an unknown one is returned as it is. */
export function countyLabel(value: string): string {
  return COUNTY_LABELS[value as County] ?? value;
}

/** Nothing is selected: every county at once, which is what the page shows by default. */
export const ALL_COUNTIES_LABEL = 'Kogu Eesti';

/** {@link prefectureOptions}' four rules over `/api/traffic/facets`' own list; the sentinel is
 * offered whether or not the facet named it, and never twice. */
export function countyOptions(
  counties: readonly string[],
  selected: string | null,
): SelectOption[] | null {
  if (counties.length === 0 && selected === null) return null;

  const known = [...new Set(counties)].filter((value) => value !== UNSPECIFIED_COUNTY);
  if (selected !== null && selected !== UNSPECIFIED_COUNTY && !known.includes(selected)) {
    known.push(selected);
  }

  const named = known
    .map((value) => ({ value, label: countyLabel(value) }))
    .sort((a, b) => ET.compare(a.label, b.label));

  return [
    { value: '', label: ALL_COUNTIES_LABEL },
    ...named,
    { value: UNSPECIFIED_COUNTY, label: COUNTY_LABELS[UNSPECIFIED_COUNTY] },
  ];
}

/**
 * Not the prefecture sentinel's sentence: this bucket is 0.85 % of the corpus, and it mostly means
 * a paragraph naming a road that crosses several counties rather than one naming no place (§7.5).
 */
export const UNSPECIFIED_COUNTY_HINT =
  'Näidatud on ainult need õnnetused, mille teatest ei õnnestunud maakonda tuvastada. Enamasti ei ole see puuduv koht, vaid nimetatud tee läbib mitut maakonda — näiteks „Tallinn-Tartu-Võru-Luhamaa mnt 141“.';

// ─── Municipality ───────────────────────────────────────────────────────────

/**
 * The one multi-select filter (D8), with three rules the county's list does not need: the value is
 * the EHAK label verbatim because four stems name both a linn and a vald (D1); options group by the
 * *gazetteer's* county, so one mis-projected row cannot split a unit into two options; and a
 * URL-only value is re-added exactly as {@link countyOptions} re-adds one (R14).
 */

/** One row of the panel: the filter value, its Estonian display form, the county group it sits in
 * (`''` for the sentinel and for a value the corpus does not know), and the window count. */
export interface MunicipalityOption {
  value: string;
  label: string;
  county: string;
  count: number;
}

/** A row as either endpoint gives it: `/api/meta`'s carries the county, a facet's does not. */
export interface MunicipalityRow {
  municipality: string;
  county?: string;
  count: number;
}

/** What the panel may offer, beside the rows themselves. */
export interface MunicipalityScope {
  /** `/api/meta`'s list — the browser's only source of a county (D2), so a row the window lost
   * still groups where it belongs rather than under `Muu`. */
  known?: readonly MunicipalityCount[];
  /** The counties whose groups are offered, or `null` for all of them. */
  counties: readonly string[] | null;
}

const LINN_SUFFIX = ' linn';

/** One collator for every Estonian sort here: `localeCompare(…, 'et')` builds a fresh one per
 * comparison, which is most of the cost of sorting 79 labels. */
const ET = new Intl.Collator('et');

/** `Tartu linn` and `Tartu vald` are two municipalities, so the suffix goes only where `others`
 * holds no same-stem vald. `Tallinn` is one word and untouched; the sentinel gets its own name. */
export function municipalityLabel(value: string, others: readonly string[] = []): string {
  if (value === UNSPECIFIED_MUNICIPALITY) return COUNTY_LABELS[UNSPECIFIED_COUNTY];
  if (!value.endsWith(LINN_SUFFIX)) return value;
  const stem = value.slice(0, -LINN_SUFFIX.length);
  return others.includes(`${stem} vald`) ? value : stem;
}

/**
 * The panel's rows, or `null` for "render no control at all" (`SelectControl`'s convention). One
 * step, because "a selected value is always visible and clearable" is one rule (R14/M5): the rows
 * are unioned with the selection, each county backfilled from `scope.known`, and the scope applied
 * last — to what is *offered*, never to what is selected.
 */
export function municipalityOptions(
  rows: readonly MunicipalityRow[] | null,
  selected: readonly string[],
  scope: MunicipalityScope,
): MunicipalityOption[] | null {
  if (rows === null) return null;
  if (rows.length === 0 && selected.length === 0) return null;

  const known = new Map((scope.known ?? []).map((row) => [row.municipality, row.county]));
  const countyOf = (name: string, own?: string): string =>
    own === undefined || own === '' ? (known.get(name) ?? '') : own;

  const named = new Map<string, MunicipalityOption>();
  let unspecified = 0;
  for (const row of rows) {
    if (row.municipality === UNSPECIFIED_MUNICIPALITY) unspecified = row.count;
    else if (!named.has(row.municipality)) {
      named.set(row.municipality, {
        value: row.municipality,
        label: row.municipality,
        county: countyOf(row.municipality, row.county),
        count: row.count,
      });
    }
  }
  for (const value of selected) {
    if (value !== UNSPECIFIED_MUNICIPALITY && !named.has(value)) {
      named.set(value, { value, label: value, county: countyOf(value), count: 0 });
    }
  }

  const values = [...named.keys()];
  const offered = [...named.values()]
    .map((row) => ({ ...row, label: municipalityLabel(row.value, values) }))
    .sort((a, b) => countyRank(a.county) - countyRank(b.county) || ET.compare(a.label, b.label));

  // Always offered and never twice, for `countyOptions`' reason: the bucket has no name of its own
  // to publish, and a reader who cannot ask for it cannot tell a small unit from a large gap.
  offered.push({
    value: UNSPECIFIED_MUNICIPALITY,
    label: municipalityLabel(UNSPECIFIED_MUNICIPALITY),
    county: '',
    count: unspecified,
  });

  // Only the sentinel is exempt from the scope: a county-less *name* under `Muu · 0` reads as a
  // measurement rather than as something the reader narrowed past.
  const within = scope.counties;
  if (within === null) return offered;
  return offered.filter(
    (row) => row.value === UNSPECIFIED_MUNICIPALITY || within.includes(row.county),
  );
}

/** The counties one prefecture covers, for the digest control's scope, or `null` when the page is
 * not narrowed to a prefecture at all (the sentinel included — it names no counties). */
export function prefectureCounties(prefecture: string | null): readonly string[] | null {
  if (prefecture === null || !(prefecture in PREFECTURE_LABELS)) return null;
  return countiesInPrefecture(prefecture as Prefecture);
}

/** The one word every panel waits in, whether it draws it or wears it as a shape's label. */
export const PENDING_LABEL = 'Laen…';

/** What a panel says when it threw while drawing rather than while fetching. Nothing was requested,
 * so the connection wording would be a lie, and a reload is the only thing that helps. */
export const RENDER_ERROR = {
  lead: (what: string): string => `${what} kuvamine ebaõnnestus.`,
  text: 'Selle osa joonistamisel läks midagi valesti. Proovi leht uuesti laadida.',
  detailLabel: 'Vea tekst, masinatekst',
} as const;

/** The ⓘ that holds a panel's provenance sentence when the panel's grain does not earn a line. */
export const PROVENANCE_LABEL = 'Allikas';

/** The panel's own field name, and the grep target of the "every page" test. */
export const MUNICIPALITY_LABEL = 'Omavalitsus';

/** The filter box above the checkboxes: 79 units is more than a reader scans. */
export const MUNICIPALITY_FILTER_PLACEHOLDER = 'Otsi omavalitsust';

/** The group the sentinel sits in, plus anything only the URL knows: not a county, so not named
 * after one. */
export const MUNICIPALITY_OTHER_GROUP_LABEL = 'Muu';

/** The one-click way out of a multi-select, which a single-select gets from its empty option. */
export const MUNICIPALITY_CLEAR_LABEL = 'Tühjenda valik';

/** Not the county sentinel's sentence: a missing municipality is usually a paragraph that named no
 * settlement, or a road and a kilometre, or a name several units share (R1 leaves those unguessed). */
export const UNSPECIFIED_MUNICIPALITY_HINT =
  'Näidatud on ainult need juhtumid, mille teatest ei õnnestunud omavalitsust tuvastada — teade ei nimetanud asulat, nimetas tee ja kilomeetri, või nimetas kohanime, mis esineb mitmes omavalitsuses.';

/** This control's one hint, keyed by the option that earns it — `CountyControl`'s shape. */
export const MUNICIPALITY_HINTS: Readonly<Record<string, string>> = {
  [UNSPECIFIED_MUNICIPALITY]: UNSPECIFIED_MUNICIPALITY_HINT,
};

/** R16: the digest pages have no facets endpoint, so their list is the whole series' and cannot
 * shrink with the window. Said out loud rather than left to be inferred from a missing count. */
export const MUNICIPALITY_LIST_SCOPE_NOTE =
  'Nimekiri on kogu selle seeria kohta, mitte valitud vahemiku ega kategooria kohta.';

/** The honest denominator (R13), from stats fetched without the municipality filter — post-filter
 * it would read "3 juhtumit 3-st", which measures the filter rather than the coverage. */
export function municipalityCoverageNote(withMunicipality: number, total: number): string {
  return `${formatCount(withMunicipality)} juhtumil ${formatCount(total)}-st on omavalitsus määratud.`;
}

/** What every filter banner says after naming its filter — the tally panels' exception is part of
 * the promise, so it is written once rather than per banner. */
const BANNER_SCOPE_CLAUSE =
  'Kõik selle lehe arvud kehtivad selle valiku, mitte kogu andmestiku kohta — välja arvatud politsei enda päevanumbrid, mis on üleriigilised.';

/** The mandatory banner for an active selection, on {@link roadUserBanner}'s pattern. */
export function municipalityBanner(labels: readonly string[]): string {
  const heading = labels.length === 1 ? 'Valitud omavalitsus' : 'Valitud omavalitsused';
  return `${heading}: ${labels.join(', ')}. ${BANNER_SCOPE_CLAUSE}`;
}

/** The clause squeezed to one sticky line (ux-spec §6 row 12). Both of its promises survive: the
 * selection scopes the page, and the police's own day tallies do not move with it. */
const BANNER_SCOPE_SHORT =
  'kõik arvud kehtivad valiku kohta; politsei päevanumbrid jäävad üleriigiliseks';

/** A banner in the two forms the sticky bar needs — the visible line and its popover's sentence. */
export function municipalityBannerClaim(labels: readonly string[]): Claim {
  const heading = labels.length === 1 ? 'Valitud omavalitsus' : 'Valitud omavalitsused';
  return {
    short: `${heading}: ${labels.join(', ')} — ${BANNER_SCOPE_SHORT}.`,
    full: municipalityBanner(labels),
  };
}

/** The county filter narrows every figure on the page it sits on, and until the redesign said so
 * only its option hint appeared — the same promise the other two filters have always printed. */
export function countyBanner(label: string): string {
  return `Valitud maakond: ${label}. ${BANNER_SCOPE_CLAUSE}`;
}

/** The digest pages' geography, which scopes them exactly as a county scopes the traffic page —
 * and which `/api/aggregates` refuses by name, so the clause's exception is the true one here too. */
export function prefectureBanner(label: string): string {
  return `Valitud prefektuur: ${label}. ${BANNER_SCOPE_CLAUSE}`;
}

export function prefectureBannerClaim(label: string): Claim {
  return {
    short: `Valitud prefektuur: ${label} — ${BANNER_SCOPE_SHORT}.`,
    full: prefectureBanner(label),
  };
}

export function countyBannerClaim(label: string): Claim {
  return {
    short: `Valitud maakond: ${label} — ${BANNER_SCOPE_SHORT}.`,
    full: countyBanner(label),
  };
}

/** Two or more active scopes collapse into one chip (ui-direction §10 Q8). The count is never the
 * whole of its text: the claim the banners exist to make is inside the chip a reader can see. */
export function scopeBannerSummary(count: number): string {
  return `${formatCount(count)} filtrit aktiivsed — ${BANNER_SCOPE_SHORT}.`;
}

/** What the sticky bar must print, given what is filtered. One function rather than a banner per
 * control, so a page that grows a filter cannot ship without the promise that filter makes. */
export interface ScopeSelection {
  prefecture?: string | null;
  county?: string | null;
  /** The municipality *labels* already selected — what the reader picked, not the stored values. */
  municipalities?: readonly string[];
  roadUser?: RoadUser | null;
}

export function scopeBanners(selection: ScopeSelection): Claim[] {
  const claims: Claim[] = [];
  const { prefecture, county, municipalities = [], roadUser } = selection;
  if (prefecture != null) claims.push(prefectureBannerClaim(prefectureLabel(prefecture)));
  if (county != null) claims.push(countyBannerClaim(countyLabel(county)));
  if (municipalities.length > 0) claims.push(municipalityBannerClaim(municipalities));
  if (roadUser != null) claims.push(roadUserBannerClaim(roadUser));
  return claims;
}

/**
 * Why the death count is a floor, with the mechanism rather than a disclaimer (§4): the log reports
 * the crash on the day it happened, and a death in hospital days later is never re-reported.
 */
export const DEATHS_FLOOR_NOTE =
  'Hukkunute arv on alampiir. Teade kirjeldab õnnetust selle toimumise päeval ja inimest, kes sureb haiglas mõni päev hiljem, ei kajastata uuesti — need surmad ei jõua siia kunagi. Ametliku liiklussurmade arvu avaldab Transpordiamet.';

/** The four road-user chips (design §6), scooter first — a locked product goal. */
export const ROAD_USER_CHIPS: readonly { value: RoadUser; label: string }[] = [
  { value: 'scooter', label: 'Tõukerattad' },
  { value: 'bicycle', label: 'Jalgrattad' },
  { value: 'motorcycle', label: 'Mootorrattad' },
  { value: 'pedestrian', label: 'Jalakäijad' },
];

/**
 * What the chip group filters on. R21: `mootorrattad` matches mopeds too — `roller` is a moped in
 * this corpus's vocabulary — and that boundary is not visible from the chip names.
 */
export const ROAD_USER_CHIPS_NOTE =
  'Filter loeb neid õnnetusi, mille teade nimetas sellist liiklejat või sõidukit. „Mootorrattad“ hõlmab ka mopeede ja rollereid.';

/** The partitive each chip takes inside {@link roadUserBanner}'s sentence. */
const ROAD_USER_BANNER_NOUNS: Readonly<Record<RoadUser, string>> = {
  scooter: 'tõukeratast',
  bicycle: 'jalgratast',
  motorcycle: 'mootorratast, mopeedi või rollerit',
  pedestrian: 'jalakäijat',
};

/**
 * The mandatory banner for an active chip (§6): the chip filters every badge, chart and facet at
 * once — except the two panels {@link POLICE_TALLY_SCOPE_NOTE} names, which it must not claim.
 */
export function roadUserBanner(value: RoadUser): string {
  return `Filter on peal: näidatud on ainult need õnnetused, mille teade nimetas ${ROAD_USER_BANNER_NOUNS[value]}. ${BANNER_SCOPE_CLAUSE}`;
}

/** The same banner in the sticky bar's two forms; the chip's own label names the road user again. */
export function roadUserBannerClaim(value: RoadUser): Claim {
  const chip = ROAD_USER_CHIPS.find((entry) => entry.value === value)?.label ?? value;
  return {
    short: `Filter peal: ${chip.toLowerCase()} — ${BANNER_SCOPE_SHORT}.`,
    full: roadUserBanner(value),
  };
}

/** The clause blocks 5 and 9 carry: `/api/aggregates` refuses `county`, `municipality` and
 * `road_user` by name, and the sentence has to name all three or it is quietly wrong. */
export const POLICE_TALLY_SCOPE_NOTE =
  'Valitud vahemik kehtib ka siin, maakonna, omavalitsuse ja liikleja filter aga mitte: politsei enda päevanumbrid on üleriigilised.';

/** A share as Estonian percent — `99.55` → `99,6%`, one decimal, comma. */
function percent(share: number): string {
  return `${share.toLocaleString('et-EE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** The page's thesis (§1, P9): both shares, the strict parser, and the mistyped-date-opener
 * mechanism — never the log's own day boundary, which explains the ±1 band alone. */
export function calibrationClaim(c: TrafficCalibration): string {
  if (c.days === 0 || c.ratio === null) {
    return 'Politsei ei ole selles andmestikus veel ühelgi päeval oma ööpäevast koguarvu avaldanud, nii et võrdlust ei ole veel millegagi teha.';
  }
  const exact = percent((c.exact_days / c.days) * 100);
  const near = percent((c.within_one_days / c.days) * 100);
  return `${formatCount(c.days)} päeval avaldas politsei ka oma enda ööpäevase koguarvu. Nendel päevadel on siin loetud õnnetusi kokku ${percent(c.ratio * 100)} politsei arvust, ${formatCount(c.exact_days)} päeval (${exact}) täpselt sama palju ja ${formatCount(c.within_one_days)} päeval (${near}) erineb arv kõige rohkem ühe võrra. Suuremad erinevused tulevad politsei enda kirjaveast: mõnes teates kannavad kõik lõigud sama vale kuupäeva, nii et terve päeva õnnetused loetakse teise päeva alla ja õigele päevale jääb auk. Kuupäev on loetud ainult range kujuga DD.MM.AAAA.`;
}

/** The ≠ strip's own words (ux-spec §2.3.4). The two figures never share a unit, so neither does
 * the wording: one counts reports the police summarised, the other incidents read out of them. */
export const TWO_METERS = {
  tallyLabel: 'Politsei kokkuvõtted',
  incidentsLabel: 'Eraldi kirjeldatud',
  reports: (n: number): string => (n === 1 ? '1 teade' : `${formatCount(n)} teadet`),
  incidents: (n: number): string => (n === 1 ? '1 juhtum' : `${formatCount(n)} juhtumit`),
  label: 'Miks need arvud erinevad',
  notEqual: 'Need kaks arvu ei ole sama mõõdik ja neid ei liideta kokku.',
} as const;

/**
 * `/kelmused`'s own words (ux-spec §2.3). The aggregate caption splits: its first sentence stays
 * visible over the bars, and the three that follow move into the Collapsible the ≠ strip shares.
 */
export const FRAUD_PAGE = {
  lede: 'Kelmusi kirjeldab politsei enamasti kokkuvõtvalt — mitu teadet nädalas laekus — ja toob välja mõne näite. Seetõttu on siin lehel esikohal politsei enda päevastatistika ja alles selle all üksikjuhtumid.',
  counterLabel: 'Kelmustega välja petetud tänavu',
  medianLead: 'Tüüpiline kahju',
  aggregateCaption:
    'Need tulbad on politsei enda päevakokkuvõtted: mitu kelmuseteadet neile laekus.',
  detailsTitle: 'Miks kaks eri arvu?',
  details:
    'Need EI ole allpool loetletud üksikjuhtumid — üks kokkuvõte katab tavaliselt kümneid juhtumeid, millest teade kirjeldab lähemalt paari. Kaht arvu ei tohi liita ega omavahel lahutada; need on sama nädala kaks eri mõõdikut. Päevakokkuvõtted on üleriigilised: prefektuuri valik neid ei kitsenda, seega jäävad tulbad samaks ka siis, kui muud lehe arvud on ühe prefektuuri omad.',
  neverAdd:
    'Allpool olevad arvud on hoopis teisest allikast: need on kokku loetud teadete tekstist eraldi tuvastatud juhtumitest. Sama nädal võib seega ülal näidata 61 teadet ja all 3 juhtumit — mõlemad on õiged, need lihtsalt loevad eri asju.',
  seriesTitle: 'Eraldi kirjeldatud kelmusi ajas',
  seriesCaption: 'Teadete tekstist tuvastatud üksikjuhtumid — mitte politsei kokkuvõtted ülal.',
  feedCaption:
    'Iga kaart on üks pressiteate lõik, politsei enda sõnadega, koos lingiga algallikale.',
} as const;

/** `/vargused` — the euro template's incidents-first half (ux-spec §2.4). */
export const THEFT_PAGE = {
  lede: 'Vargused ja sissemurdmised politsei pressiteadetes — kui palju neist teatatakse ja kui suurt kahju teated nimetavad.',
  counterLabel: 'Vargustega tekitatud kahju tänavu',
  medianLead: 'Tüüpiline vargus',
  seriesTitle: 'Vargusi ajas',
  seriesCaption:
    'Iga punkt on selle ajaühiku jooksul pressiteadetes kirjeldatud vargusjuhtumite arv.',
  damageTitle: 'Teadetes nimetatud kahju ajas',
  damageCaption:
    'Ainult need juhtumid, mille teade nimetas rahasumma — see on väike osa kõigist ülalolevatest vargustest, seega ei ole tegemist kogukahjuga. Kursoriga punktil näeb, mitmel juhtumil see summa põhineb.',
  orderLabel: 'Järjestus',
  orderCaption:
    '„Suurim kahju enne“ järjestab need juhtumid, mille teade nimetas summa; summata juhtumid jäävad nimekirja lõppu.',
} as const;

/**
 * `/lahisuhtevagivald` — the people template, and the one page with no news register at all
 * (ui-direction §10 Q7). Its year-to-date victim count is a **static** figure: no tick, no beacon,
 * no mount count-up. A ticking counter is a scoreboard device, and these are not points.
 */
export const DV_PAGE = {
  lede: 'Lähisuhtevägivald politsei pressiteadetes — ja kõrvale sama küsimus teistpidi: kui palju juhtumeid on üldse toimunud lähisuhtes, olenemata sellest, mis kuriteoks need said.',
  victimsLabel: 'Kannatanuid tänavu',
  victimsNote: 'lähisuhtevägivalla juhtumites, nii palju kui teated nimetasid',
  scopesTitle: 'Kaks vaadet',
  narrowTab: 'Lähisuhtevägivalla juhtumid',
  broadTab: 'Kõik lähisuhtekontekstiga juhtumid',
  scopeExplainer:
    'Kaks arvu erinevad, sest lähisuhte tunnus on juhtumi juures eraldi väli, mitte kategooria. Elukaaslase peksmine võib teate sõnastuses olla kehaline väärkohtlemine, ähvardamine või lähenemiskeelu rikkumine — kategooria on üks, lähisuhte tunnus aga on kõigil kolmel. Teine arv sisaldab esimest, seega neid ei liideta.',
  narrowCaption: 'Juhtumid, mille kategooriaks märgiti lähisuhtevägivald.',
  broadCaption:
    'Kõik kategooriad kokku, kus juhtum on märgitud lähisuhtes toimunuks — sama vahemik, aga oma teljestik. Kitsama vaate juhtumid on siin sees.',
  feedCaption: 'Lähisuhtevägivallaks märgitud juhtumid, politsei enda sõnadega.',
} as const;

/** `/kategooria/:category` — the shell every category without its own page inherits (ux-spec §2.4). */
export const GENERIC_PAGE = {
  lede: (label: string): string =>
    `${label} politsei pressiteadetes. Sellel kategoorial ei ole veel oma vaadet — allpool on põhinumbrid, ajajoon ja juhtumid nii, nagu politsei need kirja pani.`,
  seriesTitle: 'Juhtumeid ajas',
  seriesCaption: 'Teadete tekstist tuvastatud juhtumite arv ajaühiku kohta.',
} as const;

/** The day the police's own wording changed, and the days that kept the old one after it. The
 * sentence lives here rather than in the page because a Collapsible is where it is now read from. */
export const TRAFFIC_TALLY_BOUNDARY = '2024-08-22';
export const TRAFFIC_TALLY_STRAGGLERS = 39;

export const TRAFFIC_TALLY_WORDING_NOTE = `Sõnastus muutus ${formatDate(TRAFFIC_TALLY_BOUNDARY)}: varem «eile toimus N liiklusõnnetust», hiljem «eile registreeriti N inimkannatanutega liiklusõnnetust». Mõõdetud vahe on 0,05 õnnetust päevas, nii et joon on üks — aga piir ei ole puhas: ${formatCount(TRAFFIC_TALLY_STRAGGLERS)} päeval kasutas politsei vana sõnastust ka pärast seda kuupäeva.`;

/** The police's own scooter count, which stands beside the share read out of the reports and is
 * never added to it — the tab boundary is the source boundary, and this says so in words. */
export const SCOOTER_TALLY_CAPTION = `Politsei avaldab oma päevateates ka tõukerattaõnnetuste ööpäevast arvu — 2023. aasta kevadest alates, mitte kogu selle vahemiku ulatuses. See on politsei enda arv ja seisab eraldi kõrvaltabist, mis on teadete tekstist välja loetud. ${POLICE_TALLY_SCOPE_NOTE}`;

/** The same measurement as a trust mark (ux-spec §6 row 7): promoted in salience, demoted in
 * words. `null` where there is nothing measured yet — then the claim above is printed whole. */
export function calibrationChip(c: TrafficCalibration): string | null {
  if (c.days === 0 || c.ratio === null) return null;
  return `Kontrollitud: ${percent(c.ratio * 100)} kattuvus`;
}

/** The ⓘ beside the mark, and the words the traffic page's own demotions wear. */
export const TRAFFIC_PAGE = {
  calibrationLabel: 'Kontrolli täpsem selgitus',
  lede: 'Politsei «Liiklusinfo» päevateadetes kirjeldatud liiklusõnnetused.',
  unitLabel: 'Mida see leht mõõdab',
  unit: 'Seda lehte mõõdetakse inimestes, mitte eurodes — varakahju see seeria ei nimeta.',
  coverageTitle: 'Katvus',
  counterLabel: 'Õnnetusi tänavu',
  dispositionLabel: 'Mida «toimetati haiglasse» tähendab',
  disposition:
    'Iga juhtum siin on üks lõik politsei päevateatest. Teated kirjeldavad seda, kuhu inimene toimetati — «toimetati haiglasse» —, mitte vigastuse raskust, ja seda vahet hoiab ka see leht.',
  tallyDetailsTitle: 'Mida need tulbad loevad?',
  scooterTitle: 'Tõukerattad',
  scooterShareTab: 'Osakaal teadetest',
  scooterTallyTab: 'Politsei enda arv',
  readingTitle: 'Kuidas neid arve lugeda',
  chronicleTitle: 'Sama teema päevakroonikas',
  notOfficialTitle: 'Mida need arvud ei ole',
  impairmentTitle: 'Joobetunnused',
  impairment:
    'Joobetunnuste all on need teated, mis nimetasid alkoholi, narkootikumi või mõlemat. Joove on mainitud ligikaudu igas üheteistkümnendas teates, nii et see arv on alampiir, mitte kõigi joobes juhtide arv.',
} as const;

/** The page's loudest claim, in both tiers: the short form sits visible under the hero, the full
 * sentence in the reading Accordion below it. Neither is reworded by the components. */
export const TRAFFIC_NOT_OFFICIAL: Claim = {
  short: 'See ei ole ametlik liiklusohutuse statistika — ametliku avaldab Transpordiamet.',
  full: 'See ei ole ametlik liiklusohutuse statistika. Siit ei saa arvutada Eesti aastast liiklussurmade arvu ega õnnetuste koguarvu: leht loeb kokku selle, mida politsei oma päevateadetes kirjeldas. Ametliku statistika avaldab Transpordiamet.',
};

/**
 * The footer, on every route — the attribution and independence claims the demotion map keeps
 * globally visible (ux-spec §6 row 1). Split only where a link sits inside the sentence.
 */
export const FOOTER_ATTRIBUTION = {
  lead: 'Arvud on loetud',
  source: 'politsei.ee pressiteadetest',
  tail: 'keelemudeli abil ega ole ametlik kuriteostatistika.',
} as const;

/** Said in the footer because the wordmark is a wink at the police and the site is not theirs. */
export const FOOTER_INDEPENDENCE =
  'Politseikroonika on sõltumatu statistikaleht ega ole seotud Politsei- ja Piirivalveametiga.';

/** The leaderboard's two scales. Both ship, because raw counts crown the biggest county and per
 * capita flips it — offering one would be choosing which answer the reader gets (ux-spec §5). */
export const LEADERBOARD_MODE_LABELS = {
  total: 'Kokku',
  perCapita: '1000 el. kohta',
} as const;

/** The record plate's framing, and the whole claim it makes: the biggest sum a report *named*, not
 * the biggest loss anyone suffered. */
export const RECORD_DAMAGE_FRAMING = 'Suurim teadetes nimetatud summa sel kuul';

/** The categories a record plate may celebrate — property only, by rule rather than by whatever
 * the corpus happens to hold this month (ui-direction §6). */
export const PLATE_CATEGORIES: readonly string[] = ['THEFT', 'FRAUD', 'ROBBERY', 'PROPERTY_DAMAGE'];

/** Said where a leaderboard has no accidents to rank, instead of printing a row of zeroes. */
export const LEADERBOARD_NOTHING_READ = 'Selle vahemiku kohta pole veel ühtegi õnnetust loetud.';

/** The median pair's required denominator — a median over the reports that stated a sum is not a
 * median over incidents, and the card may not be read without the difference. */
export const MEDIAN_DENOMINATOR_NOTE = 'Mediaan summat nimetanud teadete põhjal';

/** The direct label on the sparkbar's tallest hour. */
export function HOUR_PEAK_LABEL(hour: string): string {
  return `kl ${hour}`;
}

/** The sticky filter bar's own words (ux-spec §7): the mobile trigger, the chip row, the way out. */
export const FILTER_BAR = {
  title: 'Filtrid',
  open: 'Filtrid',
  done: 'Valmis',
  clear: 'Tühjenda kõik',
  remove: 'Eemalda filter',
  scopeLabel: 'Filtri ulatus',
} as const;

/** The date popover's preset row, in ux-spec §7's order and words. */
export const DATE_PRESET_LABELS = {
  year: 'Tänavu',
  months12: '12 kuud',
  days30: '30 päeva',
  all: 'Kõik',
} as const;

export const DATE_RANGE_LABEL = 'Ajavahemik';
export const DATE_RANGE_SEPARATOR = ' – ';
export const GRANULARITY_LABEL = 'Ajaühik';

/** Where every demoted honesty text ends up, and the words that point there. */
export const METHODOLOGY_PATH = '/metoodika';
export const METHODOLOGY_LINK_LABEL = 'Loe, kuidas neid arve loetakse →';

/** The counter formula's own section, which the hero caption's popover links straight into. */
export const METHODOLOGY_COUNTER_ANCHOR = 'loendur';
export const METHODOLOGY_COUNTER_LINK = 'Kuidas loendur arvutab? →';

/** The press-release index every figure on this site is counted out of. */
export const SOURCE_URL = 'https://www.politsei.ee/et/uudised';

/** What the corpus is, in the three sentences the landing page has always led with. */
export const CORPUS_ATTRIBUTION =
  'Numbrid on kokku loetud politsei avalikest pressiteadetest keelemudeli abil. Politsei avaldab vaid osa juhtumitest ja üks teade võib kirjeldada mitut sündmust, seega ei ole need arvud ametlik kuriteostatistika. Iga juhtumi juurest pääseb algse pressiteateni.';

/** `/metoodika`, section by section: the long forms every demoted caption points at (ux-spec §1). */
export const METHODOLOGY = {
  lede: 'Siin on kirjas, kust need arvud tulevad, kui suure osa andmestikust nad katavad ja mida nendega teha ei saa.',
  corpusTitle: 'Mis need arvud on',
  coverageTitle: 'Kui palju on läbi loetud',
  extractionTitle: 'Kuidas juhtumid tekstist eraldatakse',
  extraction:
    'Iga pressiteade loetakse läbi keelemudeliga, mis eraldab tekstist üksikjuhtumid: kategooria, kuupäeva, koha ja teates nimetatud arvud. Mudel ei lisa midagi juurde — kui teade summat ei nimeta, jääb summa ka siin nimetamata. Läbi lugemata teated seisavad järjekorras ja nende päevade kohta on graafikud katkestatud, mitte nullis.',
  seriesTitle: 'Kaks seeriat, mida kokku ei liideta',
  series:
    'Politsei avaldab kaks eri teadete seeriat: päevakroonika ja «Liiklusinfo» päevateated. Need kattuvad osaliselt, nii et neid ei tohi summeerida — iga lehe arv tuleb ühest seeriast korraga ja seeria on lehe juures nimetatud.',
  talliesTitle: 'Politsei enda päevanumbrid',
  tallies:
    'Mõnel lehel on kõrvuti ka politsei enda päevakokkuvõtted: mitu teadet neile ööpäeva jooksul laekus. Need ei ole samad asjad, mis siin loetletud üksikjuhtumid — üks kokkuvõte katab tavaliselt kümneid juhtumeid, millest teade kirjeldab lähemalt paari. Kaht arvu ei tohi liita ega teineteisest lahutada.',
  counterTitle: 'Kuidas loendur arvutab',
  counter:
    'Avalehe loendurid algavad arvust, mille andmestik tegelikult katab — päevani, mille kohta on juhtumid viimati eraldatud. Sealt edasi liigub number viimase kaheteistkümne täisnädala keskmises ööpäevatempos, nii et keskööks jõuab ta välja arvestusliku päevalõpu seisuni. See on hinnang, mitte reaalajas laekuvad juhtumid: ükski juhtum ei lisandu loendurile enne, kui politsei on teate avaldanud ja see on läbi loetud. Kui andmestik on rohkem kui viis päeva maas või kui vahemikus on läbi lugemata päevi, siis loendur peatub ja näitab viimast kindlat seisu — üle lugemata päevade ei ekstrapoleerita.',
  calibrationTitle: 'Kontroll',
  limitsTitle: 'Mida need arvud ei ole',
  limits:
    'See ei ole ametlik kuriteostatistika ega ametlik liiklusohutuse statistika. Siit ei saa arvutada Eesti aastast kuritegude ega liiklussurmade arvu: leht loeb kokku selle, mida politsei oma teadetes kirjeldas. Ametliku statistika avaldavad Justiits- ja Digiministeerium ning Transpordiamet.',
} as const;

// ─── Ööpäeva kroonika ───────────────────────────────────────────────────────

/** The chronicle panel's own words (ux-spec §4). `empty` is the one affirmative sentence here and
 * may only be printed on a day both series have read — see `chronicleNotes` for the other case. */
export const CHRONICLE = {
  title: 'Ööpäeva kroonika',
  button: 'Ööpäev',
  day: 'Ööpäev',
  lastProcessed: 'viimane töödeldud päev',
  zone: 'Tallinna aja järgi',
  empty: 'Politsei ei kirjeldanud selle ööpäeva kohta ühtegi juhtumit.',
  expand: 'veel ›',
  collapse: 'vähem ‹',
  report: 'Politsei teade ↗',
  open: 'Kogu ööpäeva kroonika →',
  close: 'Sulge',
  loading: 'Laen…',
} as const;

/** The count in the teaser and the header badge — "37 juhtumit", the panel's one derived figure. */
export function chronicleCount(count: number): string {
  return count === 1 ? '1 juhtum' : `${formatCount(count)} juhtumit`;
}

/** The panel's single provenance line: both series' incident captions behind one short form,
 * neither reworded (README's two-source exception; ux-spec §4 "Provenance"). */
export const CHRONICLE_PROVENANCE: Claim = {
  short: 'Allikas: politsei päevateated; loeb keelemudel.',
  full: `${SOURCE_CAPTIONS.digest.incidents} ${SOURCE_CAPTIONS.traffic.incidents}`,
};
